import { useState, useEffect } from "react";
import { Table, Input, Tag, notification, Drawer, Select, Spin, Form } from "antd";
import { Button, Icon, Loading } from "~/components";
import { saleApi } from "~/api";
import { handleRequest, apiError, renderDateTime, strToTime } from "~/utils";
import SaleCallSourceEventForm from "./SaleCallSourceEventForm";
import SaleCallSourceUploadForm from "./SaleCallSourceUploadForm";

const WEB_STATUS_OPTIONS = [
	{ value: 0, label: 'Chưa biết' },
	{ value: 1, label: <span style={{ color: '#f5222d', fontWeight: 500 }}>Chưa có web</span> },
	{ value: 2, label: <span style={{ color: '#52c41a', fontWeight: 500 }}>Có web</span> },
];

const WEB_STATUS_COLORS = { 0: '#999', 1: '#f5222d', 2: '#52c41a' };
const WEB_STATUS_TEXT   = { 0: 'Chưa biết', 1: 'Chưa có web', 2: 'Có web' };

const CALL_STATUS = {
	0: { color: 'default', text: (_) => 'Chưa gọi' },
	1: { color: 'green',   text: (count) => count > 0 ? `Đã gọi lần ${count}` : 'Đã gọi' },
	2: { color: 'blue',    text: (_)     => 'Đã hẹn' },
};

const CALL_FILTER_OPTIONS = [
	{ value: 'all',       label: 'Tất cả' },
	{ value: '1',         label: 'Đã gọi lần 1' },
	{ value: '2',         label: 'Đã gọi lần 2' },
	{ value: '3',         label: 'Đã gọi lần 3' },
	{ value: '4',         label: 'Đã gọi lần 4' },
	{ value: '5',         label: 'Đã gọi lần 5' },
	{ value: '6',         label: 'Đã gọi lần 6' },
	{ value: '6+',        label: 'Hơn 6 lần' },
	{ value: 'appointed', label: 'Có hẹn' },
];

function SaleCallSourceDetail({ item }) {

	const [contacts, setContacts]               = useState([]);
	const [pagination, setPagination]           = useState({ page: 1, limit: 20, total: 0 });
	const [loading, setLoading]                 = useState(false);
	const [keyword, setKeyword]                 = useState('');
	const [contactEdit, setContactEdit]         = useState(null);
	const [callNote, setCallNote]               = useState('');
	const [callWebStatus, setCallWebStatus]     = useState(0);
	const [callHistory, setCallHistory]         = useState([]);
	const [historyLoading, setHistoryLoading]   = useState(false);
	const [activePanel, setActivePanel]         = useState(null);
	const [callFilter, setCallFilter]           = useState('all');

	const [addForm] = Form.useForm();

	// inline note editing
	const [editingNoteId, setEditingNoteId]       = useState(null);
	const [editingNoteValue, setEditingNoteValue] = useState('');

	// inline website editing
	const [editingWebId, setEditingWebId]       = useState(null);
	const [editingWebValue, setEditingWebValue] = useState('');

	const openCallPanel = async (record) => {
		setContactEdit(record);
		setCallNote(record.note || '');
		setCallWebStatus(record.webStatus ?? 0);
		setActivePanel('call');
		if (record.callCount > 0) {
			setHistoryLoading(true);
			let [, response] = await handleRequest(saleApi.callSourceContactHistory(record.id));
			setCallHistory(response?.data || []);
			setHistoryLoading(false);
		} else {
			setCallHistory([]);
		}
	};

	const openEventPanel = (record) => {
		setContactEdit(record);
		setActivePanel('event');
	};

	const closePanel = () => {
		setActivePanel(null);
		setCallNote('');
		setCallHistory([]);
	};

	const loadContacts = async (page = 1, kw = keyword, cf = callFilter) => {
		if (!item?.id) return;
		setLoading(true);
		let [error, response] = await handleRequest(
			saleApi.callSourceDetail(item.id, { page, limit: 20, keyword: kw, callFilter: cf })
		);
		let message = apiError('Tải danh sách thất bại', error, response);
		if (!message) {
			setContacts(response.contacts || []);
			setPagination(response.pagination || { page: 1, limit: 20, total: 0 });
		}
		setLoading(false);
	};

	useEffect(() => {
		if (item?.id) loadContacts(1, '', 'all');
	}, [item]);

	const handleSearch = (val) => {
		setKeyword(val);
		loadContacts(1, val);
	};

	const handleFilterChange = (val) => {
		setCallFilter(val);
		loadContacts(1, keyword, val);
	};

	// Cập nhật webStatus inline
	const handleWebStatusChange = async (id, value) => {
		let [error, response] = await handleRequest(
			saleApi.callSourceContactUpdate(id, { webStatus: value })
		);
		let message = apiError('Cập nhật tình trạng thất bại', error, response);
		if (!message) {
			setContacts(prev => prev.map(c => c.id === id ? { ...c, webStatus: value } : c));
		}
	};

	// Inline note
	const startNoteEdit = (record) => {
		setEditingNoteId(record.id);
		setEditingNoteValue(record.note || '');
	};

	const saveNote = async (id) => {
		let [error, response] = await handleRequest(
			saleApi.callSourceContactUpdate(id, { note: editingNoteValue })
		);
		let message = apiError('Cập nhật ghi chú thất bại', error, response);
		if (!message) {
			setContacts(prev => prev.map(c => c.id === id ? { ...c, note: editingNoteValue } : c));
		}
		setEditingNoteId(null);
	};

	// Inline website
	const startWebEdit = (record) => {
		setEditingWebId(record.id);
		setEditingWebValue(record.website || '');
	};

	const saveWebsite = async (id) => {
		let [error, response] = await handleRequest(
			saleApi.callSourceContactUpdate(id, { website: editingWebValue })
		);
		let message = apiError('Cập nhật website thất bại', error, response);
		if (!message) {
			setContacts(prev => prev.map(c => c.id === id ? { ...c, website: editingWebValue } : c));
		}
		setEditingWebId(null);
	};

	// Xác nhận đã gọi
	const handleConfirmCall = async () => {
		if (!contactEdit?.id) return;
		let [error, response] = await handleRequest(
			saleApi.callSourceContactCalled(contactEdit.id, { note: callNote, webStatus: callWebStatus })
		);
		let message = apiError('Xác nhận gọi điện thất bại', error, response);
		if (!message) {
			notification.success({ message: 'Thành công', description: 'Đã xác nhận gọi điện' });
			setContacts(prev => prev.map(c =>
				c.id === contactEdit.id ? { ...c, ...response.data } : c
			));
			closePanel();
		}
	};

	// Thêm liên hệ bằng form
	const handleAddContact = async (values) => {
		let [error, response] = await handleRequest(
			saleApi.callSourceContactAdd(item.id, values)
		);
		let message = apiError('Thêm liên hệ thất bại', error, response);
		if (!message) {
			notification.success({ message: 'Thành công', description: `Đã thêm ${values.name}` });
			addForm.resetFields();
			loadContacts(pagination.page, keyword, callFilter);
		}
	};

	// Nhập liên hệ từ Excel
	const handleImportContacts = async ({ contacts }) => {
		let [error, response] = await handleRequest(
			saleApi.callSourceContactImport(item.id, contacts)
		);
		let message = apiError('Nhập liên hệ thất bại', error, response);
		if (!message) {
			const added   = response.totalAdded   || 0;
			const skipped = response.skippedCount || 0;
			if (skipped > 0) {
				notification.warning({
					message: 'Hoàn thành',
					description: `Đã thêm ${added} liên hệ. Bỏ qua ${skipped} số trùng.`,
					duration: 6,
				});
			} else {
				notification.success({ message: 'Thành công', description: `Đã nhập ${added} liên hệ` });
			}
			closePanel();
			loadContacts(1, keyword, callFilter);
		}
	};

	// Tạo cuộc hẹn
	const handleCreateEvent = async (data, contact) => {
		data.day = strToTime(data.day);
		let [error, response] = await handleRequest(saleApi.eventAdd(data));
		let message = apiError('Tạo lịch hẹn thất bại', error, response);
		if (!message) {
			if (!response?.data?.id) {
				notification.error({ message: 'Thất bại', description: 'Chưa tạo được lịch hẹn' });
			} else {
				notification.success({ message: 'Thành công', description: `Đã tạo lịch hẹn cho ${contact?.name}` });
				let [, apptResponse] = await handleRequest(
					saleApi.callSourceContactSetAppointment(contact.id)
				);
				if (apptResponse?.data) {
					setContacts(prev => prev.map(c =>
						c.id === contact.id ? { ...c, ...apptResponse.data } : c
					));
				}
				closePanel();
			}
		}
	};

	const columns = [
		{
			title: 'STT',
			key: 'stt',
			width: 50,
			render: (_, __, i) => (pagination.page - 1) * pagination.limit + i + 1,
		},
		{ title: 'Tên khách/công ty', dataIndex: 'name',  key: 'name',  width: 170 },
		{ title: 'Số điện thoại',     dataIndex: 'phone', key: 'phone', width: 120 },
		{ title: 'Địa chỉ',           dataIndex: 'address', key: 'address', width: 150 },
		{
			title: 'Ghi chú',
			key: 'note',
			width: 180,
			render: (_, record) => {
				if (editingNoteId === record.id) {
					return (
						<div onClick={e => e.stopPropagation()}>
							<Input.TextArea
								autoSize={{ minRows: 2, maxRows: 4 }}
								value={editingNoteValue}
								onChange={e => setEditingNoteValue(e.target.value)}
								onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); saveNote(record.id); } }}
								autoFocus
								style={{ marginBottom: 4 }}
							/>
							<div className="d-flex gap">
								<Button small primary onClick={() => saveNote(record.id)}>Lưu</Button>
								<Button small white onClick={() => setEditingNoteId(null)}>Hủy</Button>
							</div>
						</div>
					);
				}
				return (
					<div style={{ cursor: 'pointer', minHeight: 22 }} onClick={() => startNoteEdit(record)} title="Click để sửa">
						{record.note
							? <span>{record.note}</span>
							: <span style={{ color: '#bbb', fontStyle: 'italic' }}>+ Thêm ghi chú</span>
						}
					</div>
				);
			},
		},
		{
			title: 'Website',
			key: 'website',
			width: 180,
			render: (_, record) => {
				if (editingWebId === record.id) {
					return (
						<div onClick={e => e.stopPropagation()}>
							<Input
								value={editingWebValue}
								onChange={e => setEditingWebValue(e.target.value)}
								onPressEnter={() => saveWebsite(record.id)}
								placeholder="https://..."
								autoFocus
								style={{ marginBottom: 4 }}
							/>
							<div className="d-flex gap">
								<Button small primary onClick={() => saveWebsite(record.id)}>Lưu</Button>
								<Button small white onClick={() => setEditingWebId(null)}>Hủy</Button>
							</div>
						</div>
					);
				}
				return (
					<div style={{ cursor: 'pointer', minHeight: 22 }} onClick={() => startWebEdit(record)} title="Click để sửa">
						{record.website
							? <a href={record.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#52c41a' }}>{record.website}</a>
							: <span style={{ color: '#bbb', fontStyle: 'italic' }}>+ Thêm website</span>
						}
					</div>
				);
			},
		},
		{
			title: 'Trạng thái',
			key: 'status',
			width: 130,
			render: (_, record) => {
				const s   = CALL_STATUS[record.status] || CALL_STATUS[0];
				const txt = s.text(record.callCount ?? 0);
				return <Tag color={s.color}>{txt}</Tag>;
			},
		},
		{
			title: 'Tình trạng web',
			key: 'webStatus',
			width: 150,
			render: (_, record) => {
				const ws = record.webStatus ?? 0;
				return (
					<Select
						value={ws}
						size="small"
						style={{ width: '100%' }}
						options={WEB_STATUS_OPTIONS}
						onChange={val => handleWebStatusChange(record.id, val)}
						labelRender={({ value }) => (
							<span style={{ color: WEB_STATUS_COLORS[value] ?? '#999' }}>
								{WEB_STATUS_TEXT[value] ?? 'Chưa biết'}
							</span>
						)}
					/>
				);
			},
		},
		{
			title: 'Thời gian gọi',
			dataIndex: 'callAt',
			key: 'callAt',
			width: 135,
			render: (callAt) => callAt > 0
				? renderDateTime(callAt)
				: <span style={{ color: '#bbb' }}>—</span>,
		},
		{
			title: '',
			key: 'action',
			width: 115,
			render: (_, record) => (
				<div className="d-flex gap">
					<Button
						small outline
						leftIcon={Icon.headphones}
						style={{ color: record.status == 2 ? '#1677ff' : record.status == 1 ? '#52c41a' : undefined }}
						onClick={() => openCallPanel(record)}
						title="Xác nhận gọi điện"
					/>
					<Button
						small blue outline
						leftIcon={Icon.calendar}
						onClick={() => openEventPanel(record)}
						title="Tạo cuộc hẹn"
					/>
				</div>
			),
		},
	];

	return (
		<div>
			{loading && <Loading />}

			<div className="d-flex gap" style={{ marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
				<Input.Search
					placeholder="Tìm theo tên hoặc số điện thoại..."
					onSearch={handleSearch}
					allowClear
					style={{ width: 300 }}
				/>
				<Select
					value={callFilter}
					onChange={handleFilterChange}
					options={CALL_FILTER_OPTIONS}
					style={{ width: 160 }}
				/>
				<div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
					<Button small outline leftIcon={Icon.plus} onClick={() => setActivePanel('add-form')}>
						Thêm liên hệ
					</Button>
					<Button small outline leftIcon={Icon.upload} onClick={() => setActivePanel('add-excel')}>
						Nhập Excel
					</Button>
				</div>
			</div>

			<Table
				columns={columns}
				dataSource={contacts}
				rowKey="id"
				size="small"
				scroll={{ x: 1280 }}
				rowClassName={(record) => {
					if (record.status == 2) return 'ant-table-row-appointed';
					if (record.status == 1) return 'ant-table-row-called';
					return '';
				}}
				pagination={{
					current: pagination.page,
					pageSize: pagination.limit,
					total: pagination.total,
					onChange: (page) => loadContacts(page),
					showTotal: (t) => `Tổng ${t} liên hệ`,
				}}
			/>

			{/* Drawer: Xác nhận gọi điện */}
			<Drawer
				title="Xác nhận gọi điện"
				open={activePanel === 'call'}
				onClose={closePanel}
				width={500}
				destroyOnClose
			>
				{contactEdit && (
					<div>
						<div style={{
							background: '#f5f5f5', borderRadius: 6,
							padding: '10px 14px', marginBottom: 14,
						}}>
							<p style={{ margin: '3px 0' }}><b>Tên:</b> {contactEdit.name}</p>
							<p style={{ margin: '3px 0' }}><b>Số điện thoại:</b> {contactEdit.phone}</p>
							{contactEdit.address && (
								<p style={{ margin: '3px 0' }}><b>Địa chỉ:</b> {contactEdit.address}</p>
							)}
						</div>
						{contactEdit.callCount > 0 && (
							<div style={{
								background: '#fff7e6', border: '1px solid #ffd591',
								borderRadius: 4, padding: '6px 12px', marginBottom: 12,
							}}>
								<span style={{ color: '#fa8c16', fontSize: 13 }}>
									Đã gọi {contactEdit.callCount} lần. Xác nhận sẽ ghi thêm lịch sử.
								</span>
							</div>
						)}
						<div className="form-group" style={{ marginBottom: 14 }}>
							<label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>Tình trạng web</label>
							<Select
								value={callWebStatus}
								onChange={setCallWebStatus}
								options={WEB_STATUS_OPTIONS}
								style={{ width: '100%' }}
								labelRender={({ value }) => (
									<span style={{ color: WEB_STATUS_COLORS[value] ?? '#999' }}>
										{WEB_STATUS_TEXT[value] ?? 'Chưa biết'}
									</span>
								)}
							/>
						</div>
						<div className="form-group" style={{ marginBottom: 16 }}>
							<label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>Ghi chú cuộc gọi</label>
							<textarea
								style={{
									width: '100%', padding: '8px 10px',
									border: '1px solid #d9d9d9', borderRadius: 4,
									fontSize: 13, resize: 'vertical', minHeight: 100,
									boxSizing: 'border-box',
								}}
								value={callNote}
								onChange={e => setCallNote(e.target.value)}
								placeholder="Kết quả cuộc gọi, ghi chú thêm..."
							/>
						</div>
						<div className="d-flex justify-content-end gap" style={{ marginBottom: 20 }}>
							<Button white leftIcon={Icon.close} onClick={closePanel}>Đóng</Button>
							<Button primary leftIcon={Icon.success} onClick={handleConfirmCall}>
								Xác nhận đã gọi
							</Button>
						</div>

						{/* Lịch sử cuộc gọi */}
						{(historyLoading || callHistory.length > 0) && (
							<div>
								<div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#555' }}>
									Lịch sử cuộc gọi
								</div>
								{historyLoading ? (
									<div style={{ textAlign: 'center', padding: 12 }}><Spin size="small" /></div>
								) : (
									<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
										{callHistory.map((h, idx) => (
											<div key={h.id} style={{
												background: '#fafafa', border: '1px solid #eee',
												borderRadius: 4, padding: '8px 12px', fontSize: 12,
											}}>
												<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
													<span style={{ color: '#888' }}>Lần {callHistory.length - idx}</span>
													<span style={{ color: '#888' }}>{renderDateTime(h.time)}</span>
												</div>
												{h.note
													? <div style={{ color: '#333' }}>{h.note}</div>
													: <div style={{ color: '#bbb', fontStyle: 'italic' }}>Không có ghi chú</div>
												}
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</div>
				)}
			</Drawer>

			{/* Drawer: Tạo cuộc hẹn */}
			<Drawer
				title={`Tạo cuộc hẹn — ${contactEdit?.name || ''}`}
				open={activePanel === 'event'}
				onClose={closePanel}
				width={600}
				destroyOnClose
			>
				<SaleCallSourceEventForm
					contact={contactEdit}
					onHandleSubmit={handleCreateEvent}
				/>
			</Drawer>

			{/* Drawer: Thêm liên hệ bằng form */}
			<Drawer
				title="Thêm liên hệ"
				open={activePanel === 'add-form'}
				onClose={() => { closePanel(); addForm.resetFields(); }}
				width={460}
				destroyOnClose
			>
				<Form form={addForm} layout="vertical" onFinish={handleAddContact}>
					<Form.Item label="Tên khách / công ty" name="name" rules={[{ required: true, message: 'Vui lòng nhập tên' }]}>
						<Input placeholder="Nguyễn Văn A / Công ty ABC" />
					</Form.Item>
					<Form.Item
						label="Số điện thoại"
						name="phone"
						rules={[
							{ required: true, message: 'Vui lòng nhập số điện thoại' },
							{ pattern: /^0\d{9,10}$/, message: 'Số điện thoại không hợp lệ (0xxxxxxxxx)' },
						]}
					>
						<Input placeholder="0901234567" />
					</Form.Item>
					<Form.Item label="Địa chỉ" name="address">
						<Input placeholder="Số nhà, đường, quận/huyện..." />
					</Form.Item>
					<Form.Item label="Website" name="website">
						<Input placeholder="https://..." />
					</Form.Item>
					<Form.Item label="Ghi chú" name="note">
						<Input.TextArea rows={3} placeholder="Ghi chú thêm..." />
					</Form.Item>
					<div className="d-flex justify-content-end gap">
						<Button white onClick={() => { closePanel(); addForm.resetFields(); }}>Đóng</Button>
						<Button primary type="submit" leftIcon={Icon.plus}>Thêm liên hệ</Button>
					</div>
				</Form>
			</Drawer>

			{/* Drawer: Nhập từ Excel */}
			<Drawer
				title="Nhập liên hệ từ Excel"
				open={activePanel === 'add-excel'}
				onClose={closePanel}
				width={760}
				destroyOnClose
			>
				<SaleCallSourceUploadForm
					mode="import"
					onHandleSubmit={handleImportContacts}
				/>
			</Drawer>
		</div>
	);
}

export default SaleCallSourceDetail;
